import { useGetDiseaseKinds } from '#api/disease_kind';
import { useGetHealthInfos } from '#api/health-infos';
import { useGetJobs } from '#api/jobs';
import { usePostMemberInfoByCoach } from '#api/member';
import { useGetPainPartKinds } from '#api/pain-part-kinds';
import { queryKeyFactory } from '#api/queryKeyFactory';
import { useGetAllTags } from '#api/tags';
import {
  QUALITY_OF_SLEEP_LIST,
  SLEEPING_HOUR_AVG_LIST
} from '#const/confirmationForms/filter';
import { APPLICATION_CAREER } from '#pages/ApplicationCarrer/const';
import { IPainPartsState } from '#pages/DashboardChartForm/states/painPartsState';
import { useToast } from '#providers/ToastProvider';
import { Input } from '#shared/Input/Input';
import { PrevHeaderBarWithTitleTemplate } from '#templates';
import {
  CreateDiseaseKindDto,
  CreateMemberInfoDto,
  CreatePainPartsDto,
  CreateSurgeryPartsDto
} from '#types/member';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import NoResult from 'src/shared/components/NoResult';
import Select from 'src/shared/components/select/Select';
import { match } from 'ts-pattern';
import InfoPainParts from './InfoPainParts';
import SaveButton from './SaveButton';
import { Item, Label, Unit } from './styles';

type ItemType = 'input' | 'select';

type Value =
  | string
  | string[]
  | CreateDiseaseKindDto
  | CreateSurgeryPartsDto
  | CreatePainPartsDto
  | number;

interface Item {
  view: string;
  type: ItemType;
  name: string | string[];
  unit?: string;
  // input이나 select 앞에 텍스트
  subView?: string | string[];
  options?: ({ label: string; value: Value }[] | undefined)[];
}

interface DashboardCustomerInfoProps {}

const DashboardCustomerInfo = ({}: DashboardCustomerInfoProps) => {
  // 직업
  const { data: jobs } = useGetJobs({
    suspense: true
  });

  const { data: diseasesData } = useGetDiseaseKinds('병력', {
    suspense: true
  });
  const { data: painPartsData } = useGetPainPartKinds('수술 이력 / 통증', {
    suspense: true
  });

  const { data: healthInfos } = useGetHealthInfos();

  const healthInfoId = healthInfos ? healthInfos.id : '';

  // PT 수강목적
  const { data: tagsData } = useGetAllTags();

  const { personId } = useParams();

  const { showToast } = useToast();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { mutate } = usePostMemberInfoByCoach({
    onSuccess: () => {
      queryClient.invalidateQueries([
        queryKeyFactory.GET_MEMBER(personId || '')
      ]);
      showToast({
        type: 'success',
        message: '사전정보가 입력되었습니다.'
      });

      navigate(-1);
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: '사전정보 입력에 실패하였습니다.'
      });
    }
  });

  const [info, setInfo] = useState<CreateMemberInfoDto>({
    jobId: '',
    healthInfo: {
      height: 0,
      weight: 0,
      sleepingHourAvg: 0,
      qualityOfSleep: 0,
      diseases: [],
      painParts: [],
      surgeryParts: []
    },
    purposeIds: []
  });

  if (!tagsData || !diseasesData || !painPartsData || !jobs)
    return <NoResult text={'사전정보가 없습니다.'}></NoResult>;

  const athleticExperienceOptions = [
    APPLICATION_CAREER.map(item => ({ label: item.text, value: item.value }))
  ];

  const purposeOptions = [
    tagsData?.data.map(item => ({ label: item.name, value: item.id }))
  ];

  const sleepOptions = [
    QUALITY_OF_SLEEP_LIST.map(item => ({
      label: item.name,
      value: item.id.toString()
    })),
    SLEEPING_HOUR_AVG_LIST.map(item => ({
      label: item.name,
      value: item.id.toString()
    }))
  ];

  const jobOptions = [
    jobs?.data.map(item => ({ label: item.name, value: item.id }))
  ];

  const diseaseOptions = [
    diseasesData?.data.map(item => ({
      label: item.name,
      value: item.id,
      diseaseKindId: item.id,
      // 회원관리 들어가면 넣기
      kindId: healthInfoId
    }))
  ];

  const surgeryPartsOptions = [
    painPartsData?.data
      .map(item => ({ label: item.name, value: item.id }))
      .concat({ label: '직접 입력', value: 'surgeryPartEtc' })
  ];

  const itemList: Item[] = [
    {
      view: '키',
      type: 'input',
      name: 'height',
      unit: 'cm'
    },
    {
      view: '몸무게',
      type: 'input',
      name: 'weight',
      unit: 'kg'
    },
    {
      view: '운동경력',
      type: 'select',
      name: 'athleticExperience',
      options: athleticExperienceOptions
    },
    {
      view: 'PT 수강목적',
      type: 'select',
      name: 'purposeIds',
      options: purposeOptions
    },
    {
      view: '수면정보',
      type: 'select',
      name: ['sleepingHourAvg', 'qualityOfSleep'],
      subView: ['수면의 질', '수면시간'],
      unit: '시간',
      options: sleepOptions
    },
    {
      view: '직업',
      type: 'select',
      name: 'jobId',
      options: jobOptions
    },
    {
      view: '병력',
      type: 'select',
      name: 'diseases',
      options: diseaseOptions
    },
    {
      view: '수술 이력',
      type: 'select',
      subView: '수술 부위',
      name: 'surgeryParts',
      options: surgeryPartsOptions
    }
  ];

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'height' || e.target.name === 'weight') {
      const name = e.target.name as 'height' | 'weight';

      setInfo(
        produce(draft => {
          (draft.healthInfo[name] as number) = Number(e.target.value);
        })
      );
    }
  };

  const onSelectChange = (value: Value, name?: string, label?: string) => {
    match(name)
      .with('athleticExperience', () => {
        const athleticValue = value as string;

        const selectedDate = dayjs().subtract(parseInt(athleticValue), 'year');
        const formattedDate = selectedDate.format('YYYY-MM-DD::HH:mm:ss');
        setInfo(
          produce(draft => {
            draft.startAthletic = formattedDate;
          })
        );
      })
      .with('purposeIds', () => {
        const purposeValue = value as string;
        setInfo(
          produce(draft => {
            draft.purposeIds.push(purposeValue);
          })
        );
      })
      .with('sleepingHourAvg', 'qualityOfSleep', () => {
        const sleepValue = Number(value);

        setInfo(
          produce(draft => {
            draft.healthInfo[name as 'sleepingHourAvg' | 'qualityOfSleep'] =
              sleepValue;
          })
        );
      })
      .with('jobId', () => {
        const jobValue = value as string;
        setInfo(
          produce(draft => {
            draft.jobId = jobValue;
          })
        );
      })
      .with('diseases', () => {
        const diseaseValue = value as string;
        setInfo(
          produce(draft => {
            if (!draft.healthInfo.diseases) {
              draft.healthInfo.diseases = [
                { diseaseKindId: diseaseValue, name: label ?? '' }
              ];
            } else {
              draft.healthInfo.diseases = [
                {
                  diseaseKindId: diseaseValue,
                  name: label ?? ''
                }
              ];
            }
          })
        );
      })
      .with('surgeryParts', () => {
        const surgeryValue = value as unknown as string;
        setInfo(
          produce(draft => {
            if (!draft.healthInfo.surgeryParts) {
              draft.healthInfo.surgeryParts;
              return;
            }
            draft.healthInfo.surgeryParts = [
              {
                surgeryPartKindId: surgeryValue,
                name: label ?? ''
              }
            ];
          })
        );
      })
      .otherwise(() => {
        console.warn(`Unhandled field name: ${name}`);
      });
  };

  const onClick = () => {
    if (!personId || !isInfoComplete()) return;
    mutate({ personId, data: info });
  };

  // 병력, 통증, 수술이력을 제외한 입력 여부 확인
  function isInfoComplete() {
    const showFailureToast = (view: string) => {
      showToast({
        type: 'fail',
        message: `${view}이(가) 입력되지 않았습니다.`
      });
    };

    const isHealthInfoValid = (name: string | number) =>
      info.healthInfo[name as keyof typeof info.healthInfo] !== undefined &&
      info.healthInfo[name as keyof typeof info.healthInfo] !== 0;

    for (const item of itemList) {
      if (Array.isArray(item.name)) {
        for (const name of item.name) {
          // sleepingHourAvg 예외 처리
          if (name === 'sleepingHourAvg') {
            if (
              info.healthInfo.sleepingHourAvg === undefined ||
              info.healthInfo.sleepingHourAvg === -1
            ) {
              if (item.subView) {
                showFailureToast(
                  item.subView[item.name.indexOf(name)] || item.view
                );
              }
              return false;
            }
          } else if (!isHealthInfoValid(name)) {
            if (!item.subView) return;
            showFailureToast(
              item.subView[item.name.indexOf(name)] || item.view
            );
            return false;
          }
        }
      } else {
        const result = match(item.name)
          .with('purposeIds', () => {
            if (info.purposeIds.length === 0) {
              showFailureToast(item.view);
              return false;
            }
            return true;
          })
          .with('jobId', () => {
            if (info.jobId === '') {
              showFailureToast(item.view);
              return false;
            }
            return true;
          })
          .with('athleticExperience', () => {
            if (info.startAthletic === undefined || info.startAthletic === '') {
              showFailureToast(item.view);
              return false;
            }
            return true;
          })
          .otherwise(name => {
            if (!isHealthInfoValid(name)) {
              showFailureToast(item.view);
              return false;
            }
            return true;
          });

        if (!result) return false;
      }
    }

    return true;
  }

  return (
    <PrevHeaderBarWithTitleTemplate
      rightContents={<SaveButton onClick={onClick} />}
      title="사전정보 기입"
    >
      <div className="px-20">
        {itemList.map(item => {
          const isOneOptions = item.options && item.options.length === 1;
          const oneOptions =
            isOneOptions && item.options ? item.options[0] : undefined;

          return (
            <Item
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
              key={item.view}
            >
              <Label>{item.view}</Label>
              {item.type === 'input' && (
                <div className="flex items-center gap-4">
                  <Input
                    name={item.name as string}
                    placeholder="-"
                    style={{
                      textAlign: 'center',
                      width: '100%',
                      height: '100%',
                      padding: '0'
                    }}
                    inputWrapperStyle={{ width: '80px', height: '35px' }}
                    onChange={onInputChange}
                  />
                  <Unit>{item.unit}</Unit>
                </div>
              )}

              {item.type === 'select' && (
                <>
                  {Array.isArray(item.name) ? (
                    <div className="flex flex-col gap-8">
                      {item.name.map((select, index) => (
                        <div
                          key={select}
                          className="flex items-center justify-between gap-8"
                        >
                          {item.subView && (
                            <div className="text-right text-[#635f6e] text-base font-normal font-['Pretendard Variable'] leading-[21px]">
                              {item.subView[index]} :
                            </div>
                          )}

                          <Select
                            defaultValue="선택"
                            options={
                              item.options
                                ? item.options[index]?.map(i => ({
                                    label: i.label,
                                    value: i.value as string
                                  })) || [{ label: '선택', value: '' }]
                                : []
                            }
                            onChange={(value, label) =>
                              onSelectChange(
                                value,
                                typeof item.name === 'string'
                                  ? item.name
                                  : (item.name[index] as string),
                                String(label)
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <SelectWithInput
                      onSelectChange={onSelectChange}
                      oneOptions={oneOptions}
                      item={item}
                      setInfo={setInfo}
                    />
                  )}
                </>
              )}
            </Item>
          );
        })}
        <InfoPainParts
          painParts={info.healthInfo.painParts || []}
          onClick={(painParts: IPainPartsState[]) => {
            setInfo(
              produce(draft => {
                draft.healthInfo.painParts = painParts;
              })
            );
          }}
        />
      </div>
    </PrevHeaderBarWithTitleTemplate>
  );
};

interface SelectWithInputProps {
  item: Item;
  oneOptions?: { label: string; value: Value }[];
  onSelectChange: (value: Value, name: string, label?: string) => void;
  setInfo: React.Dispatch<React.SetStateAction<CreateMemberInfoDto>>;
}
// 직접 입력 가능한 select
const SelectWithInput = ({
  item,
  onSelectChange,
  oneOptions,
  setInfo
}: SelectWithInputProps) => {
  // 직접 입력 input 표시 여부
  const [showInput, setShowInput] = useState('');

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const { name, value } = e.target;

    setInfo(
      produce(draft => {
        if (name === 'diseases') {
          draft.healthInfo.diseases = [
            {
              diseaseKindId: id,
              name: value
            }
          ];
        } else if (name === 'surgeryParts') {
          draft.healthInfo.surgeryParts = [
            {
              surgeryPartKindId: id,
              name: value
            }
          ];
        }
      })
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-8">
        {item.subView && (
          <div className="text-right text-[#938f9e] text-base font-normal font-['Pretendard Variable'] leading-[21px]">
            {item.subView} :
          </div>
        )}
        <Select
          defaultValue="선택"
          options={
            oneOptions?.map(item => ({
              label: item.label,
              value: item.value as string
            })) || []
          }
          onChange={(value, label) => {
            onSelectChange(value, item.name as string, String(label));

            // 직접 입력 선택시 input 표시
            const showInput =
              (item.name === 'surgeryParts' || item.name === 'diseases') &&
              label === '직접 입력'
                ? value
                : '';

            setShowInput(showInput);
          }}
        />
      </div>
      {showInput && (
        <div className="mt-4">
          <Input
            inputWrapperStyle={{ height: '45px' }}
            style={{ height: '45px' }}
            placeholder="직접 입력"
            name={item.name as string}
            onBlur={e => onInputChange(e, showInput)}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardCustomerInfo;
